/**
 * Contracts: AI-индикатор в сайдбаре (фича 031).
 *
 * Этот файл — спецификация контрактов API для фичи. На этапе implementation'а
 * содержимое будет перенесено в `packages/shared/src/contracts/ai-status.ts`
 * и зарегистрировано в `packages/shared/src/contracts/index.ts`.
 *
 * Соответствие требованиям spec.md: FR-001..FR-023, US1..US3.
 */
import { z } from "zod";
// ─────────────────────────────────────────────────────────────────────────
// GET /api/ai-status — состояние индикатора для сайдбара
// ─────────────────────────────────────────────────────────────────────────
/**
 * Статус подключения для НЕ-ПУСТОГО response'а. `no-data` живёт отдельно
 * в `AiStatusEmptyResponseSchema` — это discriminator для TS-narrowing'а.
 *
 * - `green`: последний AI-вызов успешен и в окне freshness (5 мин по дефолту).
 * - `red`: последний вызов упал.
 */
export const AiConnectionStatusSchema = z.enum(["green", "red"]);
/**
 * Response: текущее состояние AI-индикатора, когда есть хотя бы одна
 * запись `ai_usage_calls` для активной модели. Счётчики `aggregated`
 * — это SUM по всем сессиям активной модели с момента её последнего
 * сброса (FR-015), а не одной открытой сессии.
 */
export const AiStatusResponseSchema = z.object({
    /** Имя модели в том виде, как оно передаётся в API провайдера. */
    modelName: z.string(),
    /** Цветовой статус для UI (см. `AiConnectionStatus`). */
    status: AiConnectionStatusSchema,
    /**
     * Агрегированные счётчики по активной модели за период с её последнего
     * сброса (или с начала наблюдения, если сбросов не было). FR-015.
     */
    aggregated: z.object({
        /** Токены, отправленные в модель (input). */
        promptTokens: z.number().int().nonnegative(),
        /** Токены, полученные от модели (output). */
        completionTokens: z.number().int().nonnegative(),
        /** Кол-во сессий, попавших в агрегат (для дебага и tooltip'ов). */
        sessionsCount: z.number().int().nonnegative(),
        /** Timestamp последнего сброса для модели или null если не было. */
        lastResetAt: z.string().nullable(),
    }),
    /** ID текущей открытой сессии модели (для drill-down). */
    currentSessionId: z.number().int().positive().nullable(),
    /**
     * Timestamp последнего AI-вызова любого статуса для активной модели.
     * Frontend использует для тултипа «последняя активность».
     */
    lastCallAt: z.string(),
});
/**
 * Edge case: на свежем деплое до первого AI-вызова записей нет.
 * Status фиксированно `no-data` — это discriminator для union'а ниже.
 * `modelName` может быть null (ещё не определена) или строкой из env (fallback).
 */
export const AiStatusEmptyResponseSchema = z.object({
    modelName: z.string().nullable(),
    status: z.literal("no-data"),
    aggregated: z.null(),
    currentSessionId: z.null(),
    lastCallAt: z.null(),
});
/**
 * Discriminated union по полю `status` — TS narrows автоматически на
 * `status === "no-data"` ↔ AiStatusEmptyResponse, иначе AiStatusResponse.
 */
export const AiStatusEnvelopeSchema = z.discriminatedUnion("status", [
    AiStatusResponseSchema.extend({ status: z.literal("green") }),
    AiStatusResponseSchema.extend({ status: z.literal("red") }),
    AiStatusEmptyResponseSchema,
]);
// ─────────────────────────────────────────────────────────────────────────
// POST /api/ai-status/reset — сброс счётчиков текущей модели
// ─────────────────────────────────────────────────────────────────────────
/**
 * Тело запроса. Подтверждение сделано через сам факт POST (без диалога — UI
 * показывает confirm-dialog перед отправкой). Поле `expectedModelName`
 * — soft-guard: backend проверяет, что модель не сменилась между показом
 * диалога и отправкой; иначе возвращает 409.
 */
export const AiResetRequestSchema = z.object({
    /**
     * Имя модели, которое UI видел в момент показа диалога. Если на момент
     * прихода запроса в backend модель уже сменилась — сброс отклоняется
     * с 409 (закрытие предыдущей сессии произойдёт через model-switch flow,
     * не через reset). Пустая строка отклоняется на этапе валидации (400).
     */
    expectedModelName: z.string().min(1),
});
/**
 * Response: подтверждение сброса. Содержит ID нового события сброса (для
 * аудита на frontend'е, если потребуется отобразить).
 */
export const AiResetResponseSchema = z.object({
    resetId: z.number().int().positive(),
    closedSessionId: z.number().int().positive(),
    newSessionId: z.number().int().positive(),
    performedAt: z.string(),
});
/**
 * Ошибка-конфликт: модель сменилась между UI-confirm'ом и приходом запроса.
 * Frontend должен обновить состояние и предложить повторить с новым именем.
 */
export const AiResetConflictResponseSchema = z.object({
    error: z.literal("MODEL_MISMATCH"),
    expectedModelName: z.string(),
    actualModelName: z.string(),
});
// ─────────────────────────────────────────────────────────────────────────
// Стандартные ошибки
// ─────────────────────────────────────────────────────────────────────────
/**
 * Общий формат ошибки для эндпойнтов фичи. Совместим с проектным
 * `ApiErrorSchema` из `packages/shared/src/contracts/index.ts`.
 */
export const AiErrorResponseSchema = z.object({
    error: z.string(),
    message: z.string(),
});
//# sourceMappingURL=ai-status.js.map